import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { IconType } from 'react-icons';
import apiClient from '../api/client';
import { 
  FiSmartphone, 
  FiUsers, 
  FiMessageSquare,
  FiLayers,
  FiActivity,
  FiLogOut,
  FiHome,
  FiSun,
  FiMoon,
  FiGitBranch,
  FiBarChart2,
  FiBell,
  FiMap,
  FiSettings
} from 'react-icons/fi';
import { MdScience } from 'react-icons/md';
import Logo from './Logo';
import packageJson from '../../package.json';

// Get version from package.json
const APP_VERSION = packageJson.version;

interface MenuItem {
  path: string;
  label: string;
  icon: IconType;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [useAltApi, setUseAltApi] = useState(() => {
    return localStorage.getItem('use_alt_api') === 'true';
  });
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Helper to render icons with proper typing
  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  const menuItems: MenuItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: FiHome },
    { path: '/app-sessions', label: 'Uygulama Oturumları', icon: FiSmartphone },
    { path: '/game-sessions', label: 'Oyun Oturumları', icon: FiActivity },
    { path: '/chat-threads', label: 'Sohbet Konuşmaları', icon: FiMessageSquare },
    { path: '/test-sessions', label: 'Test Oturumları', icon: MdScience },
    { path: '/profile-tree', label: 'Profil Ağacı', icon: FiGitBranch },
    { path: '/meta-analytics', label: 'Meta Analytics', icon: FiBarChart2 },
    { path: '/users', label: 'Kullanıcılar', icon: FiUsers },
    { path: '/notification-center', label: 'Bildirim Merkezi', icon: FiBell },
    { path: '/road-map', label: 'Road Map', icon: FiMap },
    { path: '/settings', label: 'Ayarlar', icon: FiSettings },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-slate-900 dark:to-gray-800 overflow-hidden transition-all duration-500">
      {/* Mobile Menu Button */}
      <motion.button 
        className={`lg:hidden fixed top-4 left-4 z-[1001] p-3.5 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300
          ${theme === 'light' 
            ? 'bg-white border-2 border-gray-200 hover:border-gray-300' 
            : 'bg-gray-800 border-2 border-gray-700 hover:border-gray-600'
          }
          backdrop-blur-sm
        `}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle menu"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      >
        <div className="w-6 h-5 flex flex-col justify-between relative">
          <motion.span 
            className={`block h-0.5 w-full rounded-full transition-all duration-300 ${
              theme === 'light' ? 'bg-slate-800' : 'bg-white'
            }`}
            animate={sidebarOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
          <motion.span 
            className={`block h-0.5 w-full rounded-full transition-all duration-300 ${
              theme === 'light' ? 'bg-slate-800' : 'bg-white'
            }`}
            animate={sidebarOpen ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
          <motion.span 
            className={`block h-0.5 w-full rounded-full transition-all duration-300 ${
              theme === 'light' ? 'bg-slate-800' : 'bg-white'
            }`}
            animate={sidebarOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </motion.button>

      {/* Sidebar */}
      <motion.aside 
        className={`fixed lg:static w-72 h-screen bg-white dark:bg-slate-800 text-slate-900 dark:text-white z-[1000] shadow-2xl border-r border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden transition-colors duration-300`}
        initial={false}
        animate={{
          x: isLargeScreen ? 0 : (sidebarOpen ? 0 : -288),
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
        }}
      >
        {/* Header */}
        <motion.div 
          className="p-6 border-b border-gray-200 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-800 transition-colors duration-300"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div 
            className="flex items-center space-x-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.div 
              className="w-12 h-12 rounded-xl flex items-center justify-center bg-yellow-400"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <Logo size={40} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight transition-colors duration-300">
                Wom Analitik
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium transition-colors duration-300">
                Kontrol Paneli
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
        
        {/* Navigation - Scrollable with Staggered Animations */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 relative z-10">
          <nav className="p-4 space-y-2">
            <AnimatePresence>
              {menuItems.map((item, index) => {
                const IconComponent = item.icon as React.ComponentType<{ className?: string }>;
                const active = isActive(item.path);
                return (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -30, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.05,
                      type: 'spring',
                      stiffness: 300,
                      damping: 25,
                    }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Link
                      to={item.path}
                      className="block"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <motion.div
                        key={`${item.path}-${location.pathname}`}
                        className={`group flex items-center px-4 py-3 rounded-xl transition-colors duration-200 ${
                          active
                            ? 'bg-yellow-400 text-slate-900'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                        }`}
                        whileHover={!active ? {
                          x: 4,
                          transition: { type: 'spring', stiffness: 400, damping: 17 }
                        } : {}}
                        transition={{ duration: 0.2 }}
                      >
                        <IconComponent 
                          className={`mr-3 w-5 h-5 transition-colors duration-200 ${
                            active ? 'text-slate-900' : 'text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'
                          }`}
                        />
                        <span className={`text-sm font-medium flex-1 ${active ? 'font-semibold' : ''}`}>
                          {item.label}
                        </span>
                      </motion.div>
                    </Link>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </nav>
        </div>
        
        {/* Footer */}
        <motion.div 
          className="p-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-3 flex-shrink-0 transition-colors duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          {user && (
            <motion.div 
              className="mb-3 px-3 py-2 bg-gray-100 dark:bg-slate-700 rounded-xl transition-colors duration-300"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate transition-colors duration-300 flex-1">
                  {user.name}
                </p>
                <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-400/20 text-yellow-600 dark:text-yellow-400 border border-yellow-400/30 transition-colors duration-300 whitespace-nowrap flex-shrink-0">
                  {user.role}
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 truncate transition-colors duration-300">
                {user.email}
              </p>
            </motion.div>
          )}
          <motion.button
            onClick={logout}
            className="w-full px-4 py-2.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white rounded-xl text-sm font-medium flex items-center justify-center space-x-2 transition-colors duration-200"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {renderIcon(FiLogOut, "w-4 h-4")}
            <span>Çıkış Yap</span>
          </motion.button>
          <div className="space-y-1">
            <div className="text-xs text-slate-500 dark:text-slate-400 text-center font-medium transition-colors duration-300">
              © 2026 Womanalytics
            </div>
            <button
              onClick={() => {
                const newValue = !useAltApi;
                setUseAltApi(newValue);
                localStorage.setItem('use_alt_api', String(newValue));
                // Update API client base URL
                apiClient.updateBaseURL();
              }}
              className={`text-xs font-mono transition-colors duration-300 w-full ${
                useAltApi 
                  ? 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-semibold' 
                  : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
              } cursor-pointer`}
              title={useAltApi ? 'Alternatif API kullanılıyor (Kırmızı) - http://3.67.200.250/analytics' : 'Normal API kullanılıyor - https://api-wom.whomii.com/analytics'}
            >
              v{APP_VERSION}
            </button>
          </div>
        </motion.div>
      </motion.aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-[1001] p-3 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 hover:rotate-12 transform glass-strong border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-xl"
        aria-label="Toggle theme"
      >
        {theme === 'light' ? (
          renderIcon(FiMoon, "w-5 h-5 text-indigo-600 dark:text-indigo-400")
        ) : (
          renderIcon(FiSun, "w-5 h-5 text-yellow-500 dark:text-yellow-400")
        )}
      </button>

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 pt-20 lg:pt-6 p-4 lg:p-8 transition-all duration-300 overflow-y-auto h-screen">
        {children}
      </main>
    </div>
  );
};

export default Layout;

